import {
    Badge,
    BadgeGroup,
    FlexColumn,
    FloatingCard,
    Grid,
    GridColumn,
    StatusTimeline,
    type StatusTimelineStep,
} from '@synqo/frontend-component-library';
import { exportRunLevelCounts, exportRunResultMessages, type RunResult } from '@utils/exportRunResult';
import { getPrimaryTraceId } from '@utils/traceIdUtils';
import { type ReactNode, useMemo } from 'react';
import { describeMessageLevelCount } from '../Messages/messageLevelLabel';
import { MessageLevelIcon } from '../Messages/MessageLevelIcon';
import { getVariantByMessageLevel, type MessageVariant } from '../Messages/messageVariant';
import { ExportRunFindings } from './ExportRunFindings';
import styles from './RunDetailLayout.module.css';

const BADGE_VARIANTS: Record<MessageVariant, 'danger' | 'warning' | 'info' | 'success'> = {
    error: 'danger',
    warning: 'warning',
    info: 'info',
    success: 'success',
};

interface RunDetailLayoutProps {
    variant: MessageVariant;
    headline: string;
    detail?: string;
    /** The one thing to do next, shown beside the verdict. */
    action?: ReactNode;
    /** How far the run got; without one, the panel shows the verdict alone. */
    timeline?: StatusTimelineStep[];
    timelineLabel: string;
    /** The rows of the "Eckdaten" card, each a {@link RunDetailFact}. */
    facts?: ReactNode;
    result: RunResult;
    /** Shown in the "Meldungen" card when the run reported nothing. */
    noMessages: string;
    /** Sits beside the message counts in the header of the "Meldungen" card. */
    messagesAction?: ReactNode;
}

/** The panel of one background run: its verdict on top, how far it got and what it was beside what it reported. */
// @ai-generated 2026-10-01T12:23:39+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function RunDetailLayout({
    variant,
    headline,
    detail,
    action,
    timeline,
    timelineLabel,
    facts,
    result,
    noMessages,
    messagesAction,
}: RunDetailLayoutProps) {
    const messages = useMemo(() => exportRunResultMessages(result), [result]);
    const levelCounts = useMemo(() => exportRunLevelCounts(result), [result]);
    const traceId = getPrimaryTraceId(messages);

    return (
        <div className={styles.root}>
            <header className={styles.verdict} data-variant={variant}>
                <MessageLevelIcon variant={variant} />
                <div className={styles.verdictText}>
                    <span className={styles.verdictHeadline}>{headline}</span>
                    {detail && <span className={styles.verdictDetail}>{detail}</span>}
                </div>
                {action}
            </header>

            {timeline && (
                <Grid columns={12}>
                    <GridColumn span={5}>
                        <FlexColumn gap="md" align="stretch">
                            <FloatingCard title="Verlauf" collapsible={false}>
                                <StatusTimeline steps={timeline} label={timelineLabel} />
                            </FloatingCard>
                            <FloatingCard title="Eckdaten" collapsible={false}>
                                {facts}
                                {traceId && (
                                    <RunDetailFact
                                        label="Support-Referenz"
                                        value={<code className={styles.traceId}>{traceId}</code>}
                                    />
                                )}
                            </FloatingCard>
                        </FlexColumn>
                    </GridColumn>
                    <GridColumn span={7}>
                        <FloatingCard
                            title="Meldungen"
                            collapsible={false}
                            additionalHeaderItems={
                                <div className={styles.messageActions}>
                                    <BadgeGroup>
                                        {levelCounts.map(({ level, count }) => (
                                            <Badge
                                                key={level}
                                                size="xs"
                                                variant={BADGE_VARIANTS[getVariantByMessageLevel(level)]}
                                            >
                                                {describeMessageLevelCount(level, count)}
                                            </Badge>
                                        ))}
                                    </BadgeGroup>
                                    {messages.length > 0 && messagesAction}
                                </div>
                            }
                        >
                            {messages.length > 0 ? (
                                <div className={styles.findings}>
                                    <ExportRunFindings run={result} />
                                </div>
                            ) : (
                                <span className={styles.noMessages}>{noMessages}</span>
                            )}
                        </FloatingCard>
                    </GridColumn>
                </Grid>
            )}
        </div>
    );
}

/** One labelled value of the "Eckdaten" card. */
// @ai-generated 2026-08-28T07:59:23+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
export function RunDetailFact({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div className={styles.detail}>
            <span className={styles.detailLabel}>{label}</span>
            <span className={styles.detailValue}>{value}</span>
        </div>
    );
}
